import axios from 'axios';
import api from './api';

// Prefixes for operator-added SPR status-history entries, so the report/UI can pick them out from
// pipeline status-change entries. Keep in sync with SyncReportPage's note filter.
export const SPR_NOTE_PREFIX = '[note]';
// Stamped by ohip-to-canonical when a CC token could not be swapped: the card was removed and the
// reservation sent as cash (PLACEHOLDER). Surfaced in the Sync Report's Notes column.
export const SPR_CC_PREFIX = '[cc]';
export const SPR_DVM_PREFIX = '[dvm]';

const syncService = {
  // Get all sync projects for the current account
  getSyncProjects: async (accountId, partnerId, hotelId) => {
    const params = {};
    if (hotelId) params.hotelId = hotelId;
    else if (accountId) params.accountId = accountId;
    else if (partnerId) params.partnerId = partnerId;
    const response = await api.get('/sync', { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },

  // Get detailed information about a specific sync project
  getSyncProjectDetails: async (syncId) => {
    const response = await api.get(`/sync/${syncId}`);
    return response.data;
  },

  // Get task details including configuration
  getTaskDetails: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}`);
    return response.data;
  },

  // Get task summary statistics
  getTaskSummary: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/summary`);
    return response.data;
  },

  // Purge all S3 records for this task's scope (superadmin only)
  purgeTaskRecords: async (syncId, taskId) => {
    const response = await api.delete(`/sync/${syncId}/task/${taskId}/records`);
    return response.data;
  },

  // Send instruction to source for a task
  sendInstruction: async (syncId, taskId) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/send-instruction`);
    return response.data;
  },

  // Invoke the matched process lambda asynchronously for a task
  invokeProcess: async (syncId, taskId, settingValues = {}) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/invoke-process`, { settingValues });
    return response.data;
  },

  // Upload an offline SyncConnect export package (.hcx) for the Import Offline Extract task. Two steps, as the
  // Toolkit upload: the API names the key and returns a presigned PUT, and the file goes STRAIGHT to S3 — a package
  // can be hundreds of MB. Bare axios for the PUT: our Authorization header would invalidate the signature.
  // Run Process then imports the newest uploaded package.
  uploadOfflinePackage: async (syncId, taskId, file, onProgress) => {
    const { data } = await api.post(`/sync/${syncId}/task/${taskId}/offline-package`, { fileName: file.name });
    await axios.put(data.url, file, {
      headers: { 'Content-Type': data.contentType || 'application/octet-stream' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return data.key;
  },

  // Check an uploaded package STRAIGHT AWAY (V633): the hub decrypts it and runs the import's checks — above all that
  // the site's SyncConnect is the project's latest build. A refused package is deleted. Asynchronous on the hub (a
  // package can be hundreds of MB), so this starts the check and polls until there is a verdict.
  //   → { status: 'ACCEPTED' | 'REFUSED' | 'ERROR' | 'TIMEOUT', message, buildNumber? }
  validateOfflinePackage: async (syncId, taskId, key, { intervalMs = 3000, timeoutMs = 10 * 60 * 1000 } = {}) => {
    await api.post(`/sync/${syncId}/task/${taskId}/offline-package/validate`, { key });
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      const { data } = await api.get(`/sync/${syncId}/task/${taskId}/offline-package/validate`, { params: { key } });
      if (data && data.status && data.status !== 'CHECKING') return data;
    }
    return { status: 'TIMEOUT', message: 'The check is taking longer than expected — Run Process checks the package again before importing it.' };
  },

  // List the project's archived-index backups (superadmin-only — admin GW)
  getBackups: async (syncId) => {
    const response = await api.get(`/admin/sync/${syncId}/backups`);
    return response.data;
  },

  // Restore an archived index backup (re-indexes docs, reactivates the project). Superadmin-only — admin GW
  restoreBackup: async (syncId, backupId) => {
    const response = await api.post(`/admin/sync/${syncId}/backups/${backupId}/restore`);
    return response.data;
  },

  // Get task errors
  getTaskErrors: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/errors`);
    return response.data;
  },

  // Get reconciliation records for a task
  getReconciliationRecords: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/reconciliation`);
    return response.data;
  },

  // Get available systems for sync (source or target)
  getSyncSystems: async (role) => {
    const response = await api.get(`/sync/systems/${role}`);
    return response.data;
  },

  // Get available sync templates
  getSyncTemplates: async () => {
    const response = await api.get('/sync/templates');
    return response.data;
  },

  // Trigger a task action or update task status
  updateTask: async (syncId, taskId, taskData) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}`, taskData);
    return response.data;
  },

  // Save connector/task settings and mark task complete
  saveTaskSettings: async (syncId, taskId, settings, role, apiKey, inheritedCredentialShareId) => {
    const body = { settings, role };
    if (apiKey !== undefined && apiKey !== null) body.apiKey = apiKey;
    // SENT EVEN WHEN NULL, and omitted only when the caller genuinely has no opinion. The server reads
    // "absent" as leave-the-credential-binding-alone and "null" as unbind — collapsing the two here is
    // what unbound target connectors on saves that had nothing to do with credentials.
    if (inheritedCredentialShareId !== undefined) body.inheritedCredentialShareId = inheritedCredentialShareId;
    const response = await api.put(`/sync/${syncId}/task/${taskId}`, body);
    return response.data;
  },

  // Create a new sync project
  createSyncProject: async (projectData) => {
    const response = await api.post('/sync', projectData);
    return response.data;
  },

  // Update an existing sync project
  updateProject: async (syncId, projectData) => {
    const response = await api.put(`/sync/${syncId}`, projectData);
    return response.data;
  },

  // Update project status
  updateProjectStatus: async (syncId, status) => {
    const response = await api.put(`/sync/${syncId}`, { projectStatus: status });
    return response.data;
  },

  // SyncConnect desktop client — kick off a build for this project's source
  generateSyncConnect: async (syncId) => {
    const response = await api.post(`/sync/${syncId}/sync-connect/generate`);
    return response.data;
  },

  // Poll build status / fetch a presigned download URL for the built client
  // PUSH THE BUILT CLIENT TO THE INSTALLED ONE. The hub queues an UPDATE command on the project's own
  // queue; SyncConnect collects it on its next poll, verifies the installer's hash and installs it.
  // Nothing is sent in the body — the version, the URL and the hash come from the build itself.
  updateSyncConnect: async (syncId) => {
    const response = await api.post(`/sync/${syncId}/sync-connect/update`, {});
    return response.data;
  },

  // ── SyncConnect log files ─────────────────────────────────────────────────
  // fetch queues a LOGS command (the client uploads straight to S3 with presigned PUTs); the list reads
  // what actually landed; open returns a short-lived link to one file.
  fetchSyncConnectLogs: async (syncId, days) =>
    (await api.post(`/sync/${syncId}/sync-connect/logs`, days ? { days } : {})).data,
  getSyncConnectLogs: async (syncId) =>
    (await api.get(`/sync/${syncId}/sync-connect/logs`)).data,
  // The live stream: `after`/`before` are the `sort` cursor of a line already held.
  streamSyncConnectLogs: async (syncId, { after, before, levels, q } = {}) => {
    const p = new URLSearchParams();
    if (after) p.set('after', JSON.stringify(after));
    if (before) p.set('before', JSON.stringify(before));
    if (levels && levels.length) p.set('level', levels.join(','));
    if (q) p.set('q', q);
    const qs = p.toString();
    return (await api.get(`/sync/${syncId}/sync-connect/logs/stream${qs ? `?${qs}` : ''}`)).data;
  },
  // Ask the client to stream its log for the next hour (it is off by default).
  // SyncConnect installation status (V562): the client's health page as JSON, latest answer + waiting state.
  getSyncConnectStatusReport: async (syncId) =>
    (await api.get(`/sync/${syncId}/sync-connect/status-report`)).data,
  requestSyncConnectStatusReport: async (syncId) =>
    (await api.post(`/sync/${syncId}/sync-connect/status-report`, {})).data,
  // Superadmin: every active SyncConnect project at once.
  requestAllSyncConnectStatusReports: async () =>
    (await api.post('/sync/sync-connect/status-report', {})).data,

  // level: ERROR | WARN | INFO | DEBUG — the lowest severity the client sends (default ERROR)
  startSyncConnectStream: async (syncId, level) =>
    (await api.post(`/sync/${syncId}/sync-connect/logs/stream`, level ? { level } : {})).data,
  openSyncConnectLog: async (syncId, key) =>
    (await api.get(`/sync/${syncId}/sync-connect/logs/file?key=${encodeURIComponent(key)}`)).data,

  // fresh: true mints a NEW short link (its 24 hours start now) — what Copy link asks for. The poll does not.
  getSyncConnectDownload: async (syncId, { fresh = false } = {}) => {
    const response = await api.get(`/sync/${syncId}/sync-connect/download${fresh ? '?fresh=1' : ''}`);
    return response.data;
  },

  // Notification recipients (shared NotificationsPanel persistence — settings mode)
  listNotificationEmails: async (syncId) => {
    const response = await api.get(`/sync/${syncId}/notification-emails`);
    return response.data;
  },
  addNotificationEmail: async (syncId, data) => {
    const response = await api.post(`/sync/${syncId}/notification-emails`, data);
    return response.data;
  },
  removeNotificationEmail: async (syncId, emailId) => {
    const response = await api.delete(`/sync/${syncId}/notification-emails/${emailId}`);
    return response.data;
  },
  // The Teams webhook lives on the project row — saved via the standard project update.
  saveNotificationWebhook: async (syncId, webhookUrl) => {
    const response = await api.put(`/sync/${syncId}`, { notificationTeamsWebhook: webhookUrl });
    return response.data;
  },

  // The codes this task loaded from the property (OpenSearch, conversion-codes-<project>) — the raw list
  // the source system answered with, shown as the task's records. Not the mappings: those are
  // getConversions below.
  // A FULL REPLACEMENT. The workbook is the map: rows it does not carry are removed, which is why the
  // screen confirms it first and the server refuses a file with any required value blank.
  replaceConversionCodeRecords: async (syncId, taskId, rows) => {
    const response = await api.put(`/sync/${syncId}/task/${taskId}/conversion-codes`, { rows });
    return response.data;
  },

  // ONE ROW, edited on the screen. A PARTIAL update — only the fields sent are written, so the row keeps
  // what the load gave it (first_seen_at, still_returned, load_id). The workbook stays the tool for
  // mapping a whole chart of codes; this is for the row somebody can see is wrong.
  updateConversionCodeRecord: async (syncId, taskId, code, fields) => {
    const response = await api.patch(`/sync/${syncId}/task/${taskId}/conversion-codes`, { code, fields });
    return response.data;
  },

  getConversionCodeRecords: async (syncId, taskId, params = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const response = await api.get(`/sync/${syncId}/task/${taskId}/conversion-codes${qs.toString() ? `?${qs}` : ''}`);
    return response.data;
  },

  // Stay History Sync: code values this project's stay records sent to OHIP with no STAY conversion or default.
  getStayUnmappedCodes: async (syncId) => {
    const response = await api.get(`/sync/${syncId}/stay/unmapped-codes`);
    return response.data;
  },

  // Get conversions for a task
  getConversions: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/conversions`);
    return response.data;
  },

  // Create a new conversion
  createConversion: async (syncId, taskId, conversionData) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/conversions`, conversionData);
    return response.data;
  },

  // Update an existing conversion
  updateConversion: async (syncId, taskId, conversionUuid, conversionData) => {
    const response = await api.put(`/sync/${syncId}/task/${taskId}/conversions/${conversionUuid}`, conversionData);
    return response.data;
  },

  // Delete a conversion
  deleteConversion: async (syncId, taskId, conversionUuid) => {
    const response = await api.delete(`/sync/${syncId}/task/${taskId}/conversions/${conversionUuid}`);
    return response.data;
  },

  // Copy conversions from another project.
  //
  // `selections` is the list the wizard produced: only the conversions the other project has that this
  // one does not, and only the ones the user ticked. The server writes those and nothing else, and can
  // never overwrite a mapping this project already holds. Omitting it falls back to the old
  // copy-everything merge, which no screen sends any more.
  mergeConversionsFromProject: async (syncId, taskId, sourceProjectId, selections) => {
    // Caught HERE rather than at the server. An undefined id is dropped by JSON.stringify, so the
    // request goes out with no sourceProjectId at all and comes back 400 — a network error for what is
    // really a missing value on the caller's side, and much harder to read than this.
    if (sourceProjectId == null || sourceProjectId === '') {
      throw new Error('sourceProjectId is required to copy conversions');
    }
    const body = { sourceProjectId };
    if (Array.isArray(selections)) {
      body.selections = selections.map(c => ({
        recordType: c.recordType,
        sourceFieldName: c.sourceFieldName,
        sourceValue: c.sourceValue,
        targetValue: c.targetValue,
      }));
    }
    const response = await api.post(`/sync/${encodeURIComponent(syncId)}/task/${encodeURIComponent(taskId)}/conversions/copy-from`, body);
    return response.data;
  },

  // The single failing request/response for a record. Deliberately NOT the message log: that is
  // superadmin-only on the reconciliation table, and this returns one exchange rather than every hop
  // and payload on the record. Filtering happens on the server, so the rest is never sent.
  getFailedExchange: async (syncId, taskId, recordUuid) => {
    const res = await api.get(
      `/sync/${syncId}/task/${taskId}/reconciliation/failed-exchange?recordUuid=${encodeURIComponent(recordUuid)}`);
    return res.data;
  },

  // A presigned URL for the file a record stored (PEP extracts), minted NOW and valid 5 minutes. The
  // server reads the key from the record — the browser only names the record.
  // A file the task's last process run produced (its run report lists it) -> { url, filename, expiresIn }.
  getProcessFileLink: async (syncId, taskId, key) => {
    const res = await api.get(
      `/sync/${syncId}/task/${taskId}/process/file-link?key=${encodeURIComponent(key)}`);
    return res.data;
  },

  // variant 'excel' -> the record's Excel copy (tasks with excel_copy = 'Y') instead of the stored extract.
  getRecordFileLink: async (syncId, taskId, recordUuid, variant = null) => {
    const res = await api.get(
      `/sync/${syncId}/task/${taskId}/reconciliation/file-link?recordUuid=${encodeURIComponent(recordUuid)}`
      + (variant ? `&variant=${encodeURIComponent(variant)}` : ''));
    return res.data;
  },

  // Upload conversions CSV
  uploadConversionsCSV: async (syncId, taskId, file) => {
    const text = await file.text();
    const response = await api.post(`/sync/${syncId}/task/${taskId}/conversions/upload`, { csv: text });
    return response.data;
  },

  // Download conversions CSV
  downloadConversionsCSV: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/conversions/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Execute a task action
  executeTaskAction: async (syncId, taskId, actionName) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/actions/${actionName}`);
    return response.data;
  },

  // Test connection for a task
  testConnection: async (syncId, taskId) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/test-connection`);
    return response.data;
  },

  // Search reconciliation records with filters
  searchReconciliationRecords: async (syncId, taskId, filters) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/search`, filters);
    return response.data;
  },

  // EVERY matching record, page by page until the server says there are no more — for the CSV download and
  // the reports, which must cover the whole task rather than the newest 1,000. { download: true } returns
  // each record cut to the CSV's fields; without it records are whole (the reports read status history).
  // A failed page throws rather than returning what was fetched so far, so a partial file is never saved.
  searchAllReconciliationRecords: async (syncId, taskId, filters = {}, { download = false } = {}) => {
    const out = [];
    let after = null;
    do {
      const body = { ...filters, ...(download ? { download: true } : { paged: true }), after: after || undefined };
      const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/search`, body);
      out.push(...(response.data?.records || []));
      after = response.data?.next || null;
    } while (after);
    return out;
  },

  // On-site instructions: recipient emails (hotel / chain / partner / project contact) for the task.
  getOnsiteRecipients: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/onsite-recipients`);
    return response.data;
  },

  // Email the on-site instructions (with the CSV attached) to the chosen recipient via SES.
  sendOnsiteEmail: async (syncId, taskId, payload) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/onsite-email`, payload);
    return response.data;
  },

  // Reports (shared by the Overview report, On-site instructions, …): recipient emails for the task.
  getReportRecipients: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/report-recipients`);
    return response.data;
  },

  // Email any report (subject/body/bodyHtml + CSV attachment) via SES.
  //
  // `payload.toEmails` is the full recipient list and is SUPERADMIN-ONLY — the server rejects it from
  // anyone else. `toEmail` remains the single on-file recipient and is unchanged for everybody.
  sendReportEmail: async (syncId, taskId, payload) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/report-email`, payload);
    return response.data;
  },

  // Stop a daily report schedule (V446). Superadmin-only server-side; the id is scoped to the project
  // so a schedule from another project cannot be stopped through this route.
  stopReportSchedule: async (syncId, id) => {
    const response = await api.delete(`/sync/${syncId}/report-schedules?id=${encodeURIComponent(id)}`);
    return response.data;
  },

  // The PROJECT-level on-site instructions: every extraction task that has records, in one report.
  //
  // Built by the SERVER (/opt/core/onsite-instructions), not here — the same report is emailed on a
  // schedule and a scheduled send has no browser, so the classifiers live in one place and the screen
  // renders what it is given. `errorsOnly` drops the warnings.
  getProjectOnsiteReport: async (syncId, errorsOnly, taskId) => {
    const params = {};
    if (errorsOnly) params.errorsOnly = 1;
    // Narrows the report to one task — used by the task screen to read the server-side facts (how long
    // each group has been open, and any KB entry) that it cannot work out in the browser.
    if (taskId) params.taskId = taskId;
    const response = await api.get(`/sync/${encodeURIComponent(syncId)}/onsite-report`, {
      params: Object.keys(params).length ? params : undefined,
    });
    return response.data;
  },

  // Per-group operator annotations for a report. GET returns { annotations: { [groupKey]: {annotation,…} } }.
  getReportAnnotations: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/report-annotations`);
    return response.data;
  },

  // Upsert (empty annotation clears) an operator annotation on a report follow-up group.
  saveReportAnnotation: async (syncId, taskId, groupKey, annotation) => {
    const response = await api.put(`/sync/${syncId}/task/${taskId}/report-annotations`, { groupKey, annotation });
    return response.data;
  },

  // Superadmin: create a Jira ticket for one reconciliation record, with the message-log context
  // (input payload, OHIP exchanges up to the failure, OpenSearch lookup refs) in the description.
  // payload: { recordUuid, pageUrl?, note?, simulation? } — simulation carries a validated OHIP
  // simulator request/response the fix should reproduce.
  createJiraTicket: async (syncId, taskId, payload) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/jira`, payload);
    return response.data;
  },

  // Get S3 message log for a specific record UUID
  // all=true returns every attempt (non-deduped) for the Status History per-attempt cards; default
  // returns the deduped latest-per-step pipeline for the Messages tab.
  getReconciliationMessageLog: async (syncId, taskId, recordUuid, all = false) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/reconciliation/messagelog`, {
      params: { recordUuid, ...(all ? { all: 'true' } : {}) }
    });
    return response.data;
  },

  // Rehydrate a message-log entry's heavy transcript (ohipExchanges/request/response) that was offloaded
  // to S3. Called lazily when an entry is expanded/Simulated. Returns { ohipExchanges?, ohipRequest?, … }.
  // Every request the record made to OHIP as a bash script of curls (superadmin), built and stored server-side:
  // { url (presigned, 15 min), filename, calls, bytes, expiresAt }.
  getOhipCurlExport: async (syncId, taskId, recordUuid) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/reconciliation/ohip-curl`, { params: { recordUuid } });
    return response.data;
  },

  getReconciliationMessageLogPayload: async (syncId, taskId, bucket, key) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/reconciliation/messagelog/payload`, {
      params: { bucket, key }
    });
    return response.data;
  },

  // Superadmin: request a single source record from the source system by source id + scope (same as
  // reprocess — enqueues a MessageRequest / OHIP business event).
  requestSource: async (syncId, taskId, sourceRecordId, scopeType) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/request-source`, { sourceRecordId, scopeType });
    return response.data;
  },

  // LLM error analysis — backend groups this task's ERROR/PENDING/SKIPPED records into actions.
  // Returns { analysedCount, model, groups: [{ title, severity, action, recommendation, count, sourceIds, recordIds }] }.
  // ASYNC: returns { status:'accepted', jobId } immediately. The analysis reads up to 3000 records and
  // then calls the LLM — well past API Gateway's 29s limit, so it cannot be done in the request. Poll
  // getErrorAnalysisStatus(jobId) until status is 'done' or 'error'.
  startErrorAnalysis: async (syncId, taskId) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/error-analysis`, {});
    return response.data;
  },

  // { status: pending|running|done|error, result?, error?, recordCount? }
  getErrorAnalysisStatus: async (syncId, taskId, jobId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/error-analysis-status`, { params: { jobId } });
    return response.data;
  },

  // Bulk action on reconciliation records (skip or reprocess)
  bulkActionRecords: async (syncId, taskId, action, recordIds, profileReprocess) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/bulk-action`, {
      action,
      recordIds,
      ...(profileReprocess && profileReprocess.length ? { profileReprocess } : {})
    });
    return response.data;
  },

  // "Reprocess all in this status" — every record the search matches, on the server (sync-recon-bulk Step Function).
  // Body: { recordStatus, search, showOnlyNullTargetId }. Answers { job }; 409 with the running job if there is one.
  startReprocessAll: async (syncId, taskId, filters) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/reprocess-all`, filters);
    return response.data;
  },

  // The task's bulk job (the running one, else the newest) — { job } or { job: null }.
  getReprocessAllJob: async (syncId, taskId) => {
    const response = await api.get(`/sync/${syncId}/task/${taskId}/reconciliation/reprocess-all`);
    return response.data;
  },

  // Delete reconciliation records
  deleteReconciliationRecords: async (syncId, taskId, recordUuids) => {
    const response = await api.delete(`/sync/${syncId}/task/${taskId}/reconciliation`, {
      data: { recordUuids }
    });
    return response.data;
  },

  // Superadmin: full edit of a single reconciliation record's fields (status, target id, comment, …)
  updateReconciliationRecordFields: async (syncId, taskId, recordUuid, fields) => {
    const response = await api.put(`/sync/${syncId}/task/${taskId}/reconciliation`, {
      recordUuid,
      fields
    });
    return response.data;
  },

  // Superadmin: Postman-style OHIP request replay using the task's target connector credentials.
  // request = { method, url, headers, body, hotelId }
  ohipSimulate: async (syncId, taskId, request) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/ohip-simulate`, request);
    return response.data;
  },

  // Append a free-text comment/event to an SPR record's status history (does not change status).
  // Shows in the Message Log "Status History" tab and the reconciliation report. Callers should
  // prefix operator notes with the SPR_NOTE_PREFIX / SPR_DVM_PREFIX tags below.
  addSprComment: async (syncId, taskId, recordUuid, comment) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/comment`, {
      recordUuid,
      comment,
    });
    return response.data;
  },

  // Upload reconciliation CSV
  uploadReconciliationCSV: async (syncId, taskId, file) => {
    // Accept either the canonical "Download" export (headers: sourceRecordId, sourceDataPoint1,
    // recordStatus) OR the human-readable column headers shown in the on-screen table
    // (Source Record ID, Status, \u2026) OR a legacy raw-JSON export. Header names are normalised to
    // the canonical keys the /reconciliation/file endpoint consumes, otherwise every row is
    // silently skipped server-side (the backend only reads sourceRecordId/sourceDataPoint1/recordStatus).
    const normaliseHeader = (h) => {
      const key = h.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (key === 'sourcerecordid') return 'sourceRecordId';
      if (key === 'targetrecordid') return 'targetRecordId';
      if (key === 'sourcedatapoint1') return 'sourceDataPoint1';
      if (key === 'sourcedatapoint3') return 'sourceDataPoint3';
      if (key === 'recordstatus' || key === 'status') return 'recordStatus';
      return h; // leave unrecognised columns (Scope Type, Stage, \u2026) as-is; backend ignores them
    };
    const raw = (await file.text()).replace(/^\uFEFF/, '').trim();
    let records;
    if (raw.startsWith('[')) {
      records = JSON.parse(raw); // legacy JSON export
    } else {
      const lines = raw.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) throw new Error('File has no data rows');
      const parseRow = (line) => line.split(',').map(c => c.replace(/^"|"$/g, '').replace(/""/g, '"').trim());
      const headers = parseRow(lines[0]).map(normaliseHeader);
      records = lines.slice(1).map(line => {
        const cols = parseRow(line);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = cols[i]; });
        return obj;
      });
    }
    if (!Array.isArray(records) || records.length === 0) throw new Error('No records found in file');
    // Fail loudly rather than uploading zero rows: the endpoint returns 200 even when it skips
    // every record, so catch the "no Source Record ID column" case here.
    if (!records.some(r => r.sourceRecordId || r.sourcerecordid)) {
      throw new Error('No "Source Record ID" column found in the file. Expected a column named "Source Record ID" (or "sourceRecordId").');
    }
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/file`, records);
    return response.data;
  },

  // Download reconciliation records CSV
  downloadReconciliationCSV: async (syncId, taskId, filters) => {
    const response = await api.post(`/sync/${syncId}/task/${taskId}/reconciliation/download`, filters, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get SPR message logs for a project
  getInflight: async (syncId) => {
    const response = await api.get(`/sync/${syncId}/inflight`);
    return response.data;
  },

  getLogs: async (syncId, filters = {}) => {
    const response = await api.get(`/sync/${syncId}/logs`, { params: filters });
    return response.data;
  },

  // Initialise SPR S3 directory structure for a project (SuperAdmin only)
  initSpr: async (syncId) => {
    const response = await api.post(`/sync/${syncId}/spr-init`);
    return response.data;
  },

  // Purge all message logs for a project from S3
  purgeLogs: async (syncId) => {
    const response = await api.delete(`/sync/${syncId}/logs`);
    return response.data;
  }
};

export default syncService;
